import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import {
  SmartToy,
  Send,
  Close,
  Minimize,
  RestartAlt,
  LocalShipping,
  SupportAgent,
  Inventory2,
  Payments,
  QuestionAnswer,
} from '@mui/icons-material';

// ==============================|| TYPES ||============================== //

interface MessageType {
  id: number;
  type: 'bot' | 'user';
  message: string;
  time: string;
  loading?: boolean;
}

interface QuickActionType {
  label: string;
  value: string;
  icon: React.ReactNode;
}

interface LogisticsAIChatbotProps {
  apiUrl: string;
  token?: string;
  companyName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  botName?: string;
  userId?: string;
  shipmentData?: Record<string, any>;
}

// ==============================|| QUICK ACTIONS ||============================== //

const QUICK_ACTIONS: QuickActionType[] = [
  {
    label: 'Track Shipment',
    value: 'Track my shipment',
    icon: <LocalShipping fontSize="small" />,
  },
  {
    label: 'Create Pickup',
    value: 'Create pickup request',
    icon: <Inventory2 fontSize="small" />,
  },
  {
    label: 'Pricing & Charges',
    value: 'Show pricing and charges',
    icon: <Payments fontSize="small" />,
  },
  {
    label: 'Customer Support',
    value: 'Connect me to support',
    icon: <SupportAgent fontSize="small" />,
  },
  {
    label: 'Shipment Status',
    value: 'Show shipment status',
    icon: <QuestionAnswer fontSize="small" />,
  },
];

// ==============================|| MAIN COMPONENT ||============================== //

const LogisticsAIChatbot: React.FC<LogisticsAIChatbotProps> = ({
  apiUrl,
  token,
  companyName = 'Speedbox Logistics',
  primaryColor,
  secondaryColor,
  botName = 'Logistics AI',
  userId,
  shipmentData,
}) => {
  const theme = useTheme();

  const finalPrimary = primaryColor || theme.palette.primary.main;
  const finalSecondary = secondaryColor || theme.palette.secondary.main;

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const [messages, setMessages] = useState<MessageType[]>([
    {
      id: 1,
      type: 'bot',
      message: `👋 Welcome to ${companyName}!\n\nI am ${botName}. I can help you with shipment tracking, pickups, pricing, invoice queries, support, delivery status, and much more.`,
      time: formatTime(),
    },
  ]);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // ==============================|| AUTO SCROLL ||============================== //

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ==============================|| FORMAT TIME ||============================== //

  function formatTime() {
    return new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // ==============================|| SEND MESSAGE ||============================== //

  const sendMessage = async (customMessage?: string) => {
    const finalMessage = customMessage || input;

    if (!finalMessage.trim()) return;

    const userMessage: MessageType = {
      id: Date.now(),
      type: 'user',
      message: finalMessage,
      time: formatTime(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const loadingMessageId = Date.now() + 1;

    setMessages(prev => [
      ...prev,
      {
        id: loadingMessageId,
        type: 'bot',
        message: 'Typing...',
        time: formatTime(),
        loading: true,
      },
    ]);

    try {
      // ==============================|| BACKEND API CALL ||============================== //

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
        body: JSON.stringify({
          message: finalMessage,
          userId,
          shipmentData,
          previousMessages: messages,
          source: 'website-ai-chatbot',
          timestamp: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      setMessages(prev => {
        const filtered = prev.filter(msg => msg.id !== loadingMessageId);

        return [
          ...filtered,
          {
            id: Date.now() + 2,
            type: 'bot',
            message: data?.message || 'I have received your request successfully.',
            time: formatTime(),
          },
        ];
      });
    } catch (error) {
      console.error('Chatbot Error:', error);

      setMessages(prev => {
        const filtered = prev.filter(msg => msg.id !== loadingMessageId);

        return [
          ...filtered,
          {
            id: Date.now() + 3,
            type: 'bot',
            message: '⚠️ Unable to connect with AI server. Please try again later.',
            time: formatTime(),
          },
        ];
      });
    } finally {
      setLoading(false);
    }
  };

  // ==============================|| RESET CHAT ||============================== //

  const resetChat = () => {
    setMessages([
      {
        id: 1,
        type: 'bot',
        message: `👋 Welcome to ${companyName}!\n\nI am ${botName}. How can I assist you today?`,
        time: formatTime(),
      },
    ]);
  };

  // ==============================|| CHAT HEIGHT ||============================== //

  const chatbotHeight = useMemo(() => {
    if (minimized) return 72;

    return 650;
  }, [minimized]);

  return (
    <>
      {/* ==============================|| BOT ICON ||============================== */}

      <Fade in>
        <Box
        // sx={{
        //   position: 'fixed',
        //   bottom: 24,
        //   right: 96,
        //   zIndex: 9999,
        // }}
        >
          {!open && (
            <Tooltip title="AI Assistant">
              <IconButton
                onClick={() => setOpen(true)}
                sx={{
                  width: 48,
                  height: 48,
                  backdropFilter: 'blur(16px)',
                  background: `linear-gradient(135deg, ${alpha(finalPrimary, 0.9)}, ${alpha(finalSecondary, 0.9)})`,
                  boxShadow: `0px 10px 30px ${alpha(finalPrimary, 0.35)}`,
                  border: `1px solid ${alpha('#ffffff', 0.2)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px) scale(1.05)',
                    boxShadow: `0px 14px 40px ${alpha(finalPrimary, 0.45)}`,
                  },
                }}
              >
                <Badge
                  color="success"
                  variant="dot"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                >
                  <SmartToy
                    sx={{
                      color: '#fff',
                      fontSize: 34,
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Fade>

      {/* ==============================|| CHAT WINDOW ||============================== */}

      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: {
              xs: 'calc(100vw - 24px)',
              sm: 420,
            },
            height: chatbotHeight,
            borderRadius: '28px',
            overflow: 'hidden',
            zIndex: 9999,
            border: `1px solid ${alpha('#ffffff', 0.18)}`,
            background: alpha('#ffffff', 0.08),
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            boxShadow: `0px 20px 60px ${alpha(finalPrimary, 0.25)}`,
            transition: 'all 0.35s ease',
          }}
        >
          {/* ==============================|| HEADER ||============================== */}

          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${alpha('#ffffff', 0.12)}`,
              background: `linear-gradient(135deg, ${alpha(finalPrimary, 0.95)}, ${alpha(finalSecondary, 0.95)})`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  background: alpha('#fff', 0.2),
                  border: `1px solid ${alpha('#fff', 0.25)}`,
                }}
              >
                <SmartToy />
              </Avatar>

              <Box>
                <Typography variant="h6" fontWeight={700} color="#fff">
                  {botName}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: alpha('#fff', 0.8),
                  }}
                >
                  AI Logistics Assistant • Online
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Reset Chat">
                <IconButton
                  onClick={resetChat}
                  sx={{
                    color: '#fff',
                  }}
                >
                  <RestartAlt />
                </IconButton>
              </Tooltip>

              <Tooltip title="Minimize">
                <IconButton
                  onClick={() => setMinimized(prev => !prev)}
                  sx={{
                    color: '#fff',
                  }}
                >
                  <Minimize />
                </IconButton>
              </Tooltip>

              <Tooltip title="Close">
                <IconButton
                  onClick={() => {
                    setOpen(false);
                    setMinimized(false);
                  }}
                  sx={{
                    color: '#fff',
                  }}
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* ==============================|| BODY ||============================== */}

          {!minimized && (
            <>
              <Box
                sx={{
                  flex: 1,
                  height: 'calc(100% - 160px)',
                  overflowY: 'auto',
                  px: 2,
                  py: 2,
                  '&::-webkit-scrollbar': {
                    width: 0,
                  },
                }}
              >
                {/* ==============================|| QUICK ACTIONS ||============================== */}

                <Stack
                  direction="row"
                  spacing={1}
                  //   useFlexGap
                  flexWrap="wrap"
                  mb={2}
                >
                  {QUICK_ACTIONS.map((action, index) => (
                    <Chip
                      key={index}
                      icon={action.icon as any}
                      label={action.label}
                      clickable
                      onClick={() => sendMessage(action.value)}
                      sx={{
                        borderRadius: '14px',
                        color: theme.palette.text.primary,
                        background: alpha('#ffffff', 0.08),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha('#ffffff', 0.12)}`,
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          background: alpha(finalPrimary, 0.15),
                          transform: 'translateY(-2px)',
                        },
                      }}
                    />
                  ))}
                </Stack>

                {/* ==============================|| MESSAGES ||============================== */}

                <Stack spacing={2}>
                  {messages.map(msg => (
                    <Box key={msg.id} display="flex" justifyContent={msg.type === 'user' ? 'flex-end' : 'flex-start'}>
                      <Box
                        sx={{
                          maxWidth: '85%',
                          px: 2,
                          py: 1.5,
                          borderRadius: msg.type === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          background:
                            msg.type === 'user'
                              ? `linear-gradient(135deg, ${alpha(finalPrimary, 0.9)}, ${alpha(finalSecondary, 0.9)})`
                              : alpha('#ffffff', 0.08),
                          border: `1px solid ${alpha('#ffffff', 0.12)}`,
                          backdropFilter: 'blur(12px)',
                          boxShadow: `0px 6px 20px ${alpha('#000', 0.08)}`,
                        }}
                      >
                        {msg.loading ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} />

                            <Typography variant="body2">AI is typing...</Typography>
                          </Stack>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.8,
                              color: msg.type === 'user' ? '#fff' : theme.palette.text.primary,
                            }}
                          >
                            {msg.message}
                          </Typography>
                        )}

                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 1,
                            opacity: 0.65,
                            textAlign: 'right',
                            color: msg.type === 'user' ? '#fff' : theme.palette.text.secondary,
                          }}
                        >
                          {msg.time}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <div ref={messagesEndRef} />
              </Box>

              {/* ==============================|| FOOTER ||============================== */}

              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${alpha('#ffffff', 0.1)}`,
                  background: alpha('#ffffff', 0.04),
                }}
              >
                <TextField
                  fullWidth
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !loading) {
                      sendMessage();
                    }
                  }}
                  placeholder="Ask about shipments, invoices, tracking..."
                  variant="outlined"
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '18px',
                      background: alpha('#ffffff', 0.08),
                      backdropFilter: 'blur(10px)',
                      pr: 0.5,
                      '& fieldset': {
                        borderColor: alpha('#ffffff', 0.12),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(finalPrimary, 0.45),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: finalPrimary,
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => sendMessage()}
                          disabled={loading || !input.trim()}
                          sx={{
                            width: 46,
                            height: 46,
                            background: `linear-gradient(135deg, ${finalPrimary}, ${finalSecondary})`,
                            color: '#fff',
                            '&:hover': {
                              opacity: 0.9,
                            },
                            '&.Mui-disabled': {
                              opacity: 0.5,
                              color: '#fff',
                            },
                          }}
                        >
                          <Send />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </>
          )}
        </Paper>
      </Slide>
    </>
  );
};

export default LogisticsAIChatbot;
